'use client';

import type { MysteryRoomHeroState, MysteryRoomInventoryItemState, MysteryRoomLocalRuntime } from '../core/localRuntime';
import { upsertIncidentFromAnchor } from './incidentSystem';
import { applyLocalTriggerActions } from './triggerSystem';
import { isCircleVisible, isRectVisible } from './visibilitySystem';
import { canDoorActorUseDoor, doorAccessRequirementLabel, type DoorActor } from './doorSystem';
import { rectCenter } from '../world/mapTypes';
import {
  MYSTERY_ROOM_FLASHLIGHT_ASSETS,
  MYSTERY_ROOM_GLOCK19_ASSETS,
  getMysteryRoomAccessCardVisual,
} from '../world/itemAssets';
import {
  MYSTERY_ROOM_RESOURCE_DEFINITIONS,
  mysteryRoomResourceItemId,
} from '../world/resourceDefinitions';

export type MysteryRoomHeroActionId =
  | 'overload_server'
  | 'sabotage_door'
  | 'trigger_false_alarm';

export type MysteryRoomHeroAction = {
  id: MysteryRoomHeroActionId;
  label: string;
  anchorId: string;
  zoneId: string;
  distance: number;
};

const HERO_ACTION_RADIUS = 110;
const INTERACTABLE_VISIBILITY_RADIUS = 18;
const DOOR_INTERACTION_RADIUS = 150;
const NPC_ATTACK_RADIUS = 92;

type InventoryItemDefinition = Omit<MysteryRoomInventoryItemState, 'acquiredAtMs' | 'quantity' | 'slotIndex'> & {
  quantity?: number;
};

const INVENTORY_ITEM_DEFINITIONS: Record<string, InventoryItemDefinition> = {
  stun_baton: {
    id: 'stun_baton',
    label: 'Электрошоковая дубинка',
    kind: 'weapon',
    isWeapon: true,
    icon: 'SB',
    description: 'Контактное оружие для нейтрализации NPC рядом с героем.',
  },
  flashlight: {
    id: 'flashlight',
    label: 'Фонарик',
    kind: 'tool',
    isWeapon: false,
    icon: 'FL',
    iconAssetUrl: MYSTERY_ROOM_FLASHLIGHT_ASSETS.inventoryUrl,
    iconAssetName: MYSTERY_ROOM_FLASHLIGHT_ASSETS.inventoryName,
    iconAssetMimeType: MYSTERY_ROOM_FLASHLIGHT_ASSETS.mimeType,
    description: 'Дает направленный конус света в темных комнатах и коридорах.',
  },
  pistol: {
    id: 'pistol',
    label: 'Пистолет',
    kind: 'weapon',
    isWeapon: true,
    icon: 'PS',
    iconAssetUrl: MYSTERY_ROOM_GLOCK19_ASSETS.inventoryUrl,
    iconAssetName: MYSTERY_ROOM_GLOCK19_ASSETS.inventoryName,
    iconAssetMimeType: MYSTERY_ROOM_GLOCK19_ASSETS.mimeType,
    description: 'Дальнобойное оружие. Активируйте в инвентаре и стреляйте левой кнопкой мыши.',
  },
  medkit: {
    id: 'medkit',
    label: 'Аптечка',
    kind: 'tool',
    isWeapon: false,
    icon: 'MED',
    description: 'Восстанавливает здоровье героя через слот быстрого доступа.',
  },
};

export type MysteryRoomPrimaryLocalAction =
  | {
      kind: 'npc_attack';
      label: string;
      detail: string;
      targetId: string;
    }
  | {
      kind: 'npc_requires_weapon';
      label: string;
      detail: string;
      targetId: string;
    }
  | {
      kind: 'interactable';
      label: string;
      detail: string;
      targetId: string;
    }
  | {
      kind: 'door';
      label: string;
      detail: string;
      targetId: string;
    };
const HERO_ACTION_RULES: Record<
  string,
  {
    id: MysteryRoomHeroActionId;
    label: string;
    severity: number;
    eventMessage: string;
  }
> = {
  server_fault_main: {
    id: 'overload_server',
    label: 'Перегрузить сервер',
    severity: 0.72,
    eventMessage: 'Игрок перегрузил сервер',
  },
  door_failure_server: {
    id: 'sabotage_door',
    label: 'Саботировать дверь',
    severity: 0.65,
    eventMessage: 'Игрок саботировал дверь',
  },
  alarm_cross: {
    id: 'trigger_false_alarm',
    label: 'Ложная тревога',
    severity: 1,
    eventMessage: 'Игрок запустил ложную тревогу',
  },
};

export function performPrimaryLocalInteraction(
  runtime: MysteryRoomLocalRuntime,
  options?: { p2pGuestDoors?: boolean },
): MysteryRoomLocalRuntime {
  if (runtime.gameStatus !== 'running' || !runtime.hero.alive) {
    return runtime;
  }

  const attackableNpc = getNearestLocalNpcInAttackRange(runtime);
  if (attackableNpc && hasInventoryWeapon(runtime)) {
    return neutralizeLocalNpc(runtime, attackableNpc.id);
  }

  const interactable = getNearestLocalInteractable(runtime);
  if (!interactable) {
    const door = getNearestLocalDoor(runtime);
    if (door) {
      return options?.p2pGuestDoors ? toggleLocalDoorP2PGuest(runtime, door.id) : toggleLocalDoor(runtime, door.id);
    }

    return attackableNpc ? appendLocalEvent(runtime, 'Нужно оружие в инвентаре') : runtime;
  }

  return performLocalInteraction(runtime, interactable.id);
}

/** P2P-гость: открыть/закрыть без локальной проверки пропусков (инвентарь авторитетен у хоста). */
export function toggleLocalDoorP2PGuest(runtime: MysteryRoomLocalRuntime, doorId: string): MysteryRoomLocalRuntime {
  const door = runtime.doors.find((entry) => entry.id === doorId);
  if (!door) return runtime;
  const shouldClose = door.state === 'open' || door.state === 'opening';
  const next = applyRemoteP2pDoorToggle(runtime, doorId);
  if (next === runtime) return runtime;
  return {
    ...next,
    events: [`Дверь ${shouldClose ? 'закрывается' : 'открывается'}: ${door.label}`, ...next.events].slice(0, 10),
  };
}

export function performLocalInteraction(
  runtime: MysteryRoomLocalRuntime,
  interactableId: string,
): MysteryRoomLocalRuntime {
  if (runtime.gameStatus !== 'running' || !runtime.hero.alive) {
    return runtime;
  }

  const interactable = runtime.interactables.find((entry) => entry.id === interactableId);
  if (!interactable) {
    return runtime;
  }

  if (isConsumedInteractable(interactable)) {
    return appendLocalEvent(runtime, `${interactable.label}: уже забрано`);
  }

  if (interactable.zoneId !== runtime.hero.zoneId) {
    return appendLocalEvent(runtime, `${interactable.label}: объект в другом секторе`);
  }

  const distance = Math.hypot(
    runtime.hero.position.x - interactable.anchor.x,
    runtime.hero.position.y - interactable.anchor.y,
  );
  if (distance > interactable.interactionRadius) {
    return appendLocalEvent(runtime, `${interactable.label}: подойди ближе`);
  }

  if (interactable.requiredAccess && !runtime.hero.accessLevels.includes(interactable.requiredAccess)) {
    return appendLocalEvent(runtime, `${interactable.label}: доступ запрещён`);
  }

  if (interactable.source === 'custom_object') {
    return activateCustomObjectInteractable(runtime, interactableId);
  }

  switch (interactable.kind) {
    case 'access_grant':
      return applyAccessGrant(runtime, interactableId);
    case 'hide_zone':
      return toggleHideZone(runtime, interactableId);
    case 'inspect':
      return applyInspect(runtime, interactableId);
    case 'pickup':
      return pickupInventoryItem(runtime, interactableId);
    case 'light_switch':
      return toggleZoneLights(runtime, interactableId);
    default:
      return runtime;
  }
}

/** P2P-хост/гость: синхродроп подобранных объектов без лишних побочных эффектов на чужом клиенте. */
export function applyAuthoritativeUsedInteractables(
  runtime: MysteryRoomLocalRuntime,
  usedIds: readonly string[],
): MysteryRoomLocalRuntime {
  if (usedIds.length === 0) {
    return runtime;
  }
  const usedSet = new Set(usedIds);
  return {
    ...runtime,
    interactables: runtime.interactables.map((entry) =>
      usedSet.has(entry.id) && entry.state !== 'used' ? { ...entry, state: 'used' as const } : entry,
    ),
  };
}

/** Контекст хоста: инвентарь и тень гостя для авторитетного взаимодействия */
export type MysteryRoomP2pGuestInteractHostContext = {
  guestPeerId: string;
  guestInventories: { current: Map<string, MysteryRoomInventoryItemState[]> };
  guestShadows: { current: Map<string, MysteryRoomHeroState> };
};

export type MysteryRoomP2pHostGuestStateBundle = {
  guestInventories: { current: Map<string, MysteryRoomInventoryItemState[]> };
  guestShadowHeroes: { current: Map<string, MysteryRoomHeroState> };
};

function upsertGuestInventoryItem(
  ctx: MysteryRoomP2pGuestInteractHostContext,
  runtime: MysteryRoomLocalRuntime,
  itemDefinition: InventoryItemDefinition,
): void {
  const list = [...(ctx.guestInventories.current.get(ctx.guestPeerId) ?? [])];
  const existingItem = list.find((item) => item.id === itemDefinition.id);
  const amount = normalizePickupQuantity(itemDefinition.quantity);
  const nextQuantity = Math.max(0, existingItem?.quantity ?? 0) + amount;
  const nextList = existingItem
    ? list.map((item) => (item.id === itemDefinition.id ? { ...item, quantity: nextQuantity } : item))
    : [
        ...list,
        {
          ...itemDefinition,
          acquiredAtMs: runtime.elapsedMs,
          quantity: amount,
          slotIndex: list.length,
        },
      ];
  ctx.guestInventories.current.set(
    ctx.guestPeerId,
    nextList.map((item, slotIndex) => ({ ...item, slotIndex })),
  );
}

function applyPickupAccessGrantToGuestShadow(
  ctx: MysteryRoomP2pGuestInteractHostContext,
  grantsAccess: NonNullable<MysteryRoomLocalRuntime['interactables'][number]['grantsAccess']>,
): void {
  const sh = ctx.guestShadows.current.get(ctx.guestPeerId);
  if (!sh || sh.accessLevels.includes(grantsAccess)) {
    return;
  }
  ctx.guestShadows.current.set(ctx.guestPeerId, {
    ...sh,
    accessLevels: [...sh.accessLevels, grantsAccess],
  });
}

function activateCustomObjectInteractableForGuest(
  runtime: MysteryRoomLocalRuntime,
  interactableId: string,
  ctx: MysteryRoomP2pGuestInteractHostContext,
): MysteryRoomLocalRuntime {
  const interactable = runtime.interactables.find((entry) => entry.id === interactableId);
  if (!interactable) {
    return runtime;
  }
  const object = runtime.map.customObjects.find((entry) => entry.id === interactable.customObjectId);
  const actions =
    interactable.kind === 'pickup' && interactable.onPickupActions?.length
      ? interactable.onPickupActions
      : interactable.onActivateActions ?? [];
  const sourceRect = object?.rect ?? {
    x: interactable.anchor.x - 8,
    y: interactable.anchor.y - 8,
    width: 16,
    height: 16,
  };
  if (interactable.kind === 'pickup') {
    upsertGuestInventoryItem(
      ctx,
      runtime,
      inventoryDefinitionForInteractable(runtime, interactable, interactable.itemId ?? object?.id ?? interactable.id, object),
    );
  }
  if (interactable.kind === 'pickup' && interactable.grantsAccess) {
    applyPickupAccessGrantToGuestShadow(ctx, interactable.grantsAccess);
  }
  const next =
    actions.length > 0
      ? applyLocalTriggerActions(
          runtime,
          {
            id: interactable.customObjectId ?? interactable.id,
            label: interactable.label,
            zoneId: interactable.zoneId,
            rect: sourceRect,
            position: interactable.anchor,
          },
          actions,
        )
      : interactable.kind === 'pickup'
        ? runtime
        : appendLocalEvent(runtime, `Активировано: ${interactable.label}`);

  return {
    ...next,
    interactables: next.interactables.map((entry) =>
      entry.id === interactableId && interactable.kind === 'pickup' ? { ...entry, state: 'used' as const } : entry,
    ),
  };
}

function pickupInventoryItemOnHostForGuest(
  runtime: MysteryRoomLocalRuntime,
  interactableId: string,
  ctx: MysteryRoomP2pGuestInteractHostContext,
): MysteryRoomLocalRuntime {
  const interactable = runtime.interactables.find((entry) => entry.id === interactableId);
  if (!interactable || (!interactable.itemId && !interactable.resourceKind)) {
    return runtime;
  }
  upsertGuestInventoryItem(
    ctx,
    runtime,
    inventoryDefinitionForInteractable(
      runtime,
      interactable,
      interactable.itemId ?? (interactable.resourceKind ? mysteryRoomResourceItemId(interactable.resourceKind) : interactable.id),
    ),
  );
  return {
    ...runtime,
    interactables: runtime.interactables.map((entry) =>
      entry.id === interactableId ? { ...entry, state: 'used' as const } : entry,
    ),
  };
}

function applyAccessGrantOnHostForGuest(
  runtime: MysteryRoomLocalRuntime,
  interactableId: string,
  ctx: MysteryRoomP2pGuestInteractHostContext,
): MysteryRoomLocalRuntime {
  const interactable = runtime.interactables.find((entry) => entry.id === interactableId);
  if (!interactable) {
    return runtime;
  }
  const itemId = interactable.itemId ?? interactable.id;
  const sh = ctx.guestShadows.current.get(ctx.guestPeerId);
  const alreadyHasAccess = interactable.grantsAccess ? sh?.accessLevels.includes(interactable.grantsAccess) ?? false : true;
  upsertGuestInventoryItem(ctx, runtime, inventoryDefinitionForInteractable(runtime, interactable, itemId));
  if (interactable.grantsAccess && !alreadyHasAccess) {
    applyPickupAccessGrantToGuestShadow(ctx, interactable.grantsAccess);
  }
  return {
    ...runtime,
    interactables: runtime.interactables.map((entry) =>
      entry.id === interactableId ? { ...entry, state: 'used' as const } : entry,
    ),
  };
}

function toggleHideZoneOnHostForGuest(
  runtime: MysteryRoomLocalRuntime,
  interactableId: string,
  ctx: MysteryRoomP2pGuestInteractHostContext,
): MysteryRoomLocalRuntime {
  const sh = ctx.guestShadows.current.get(ctx.guestPeerId);
  if (!sh) {
    return runtime;
  }
  const isSameHideZone = sh.hideZoneId === interactableId;
  ctx.guestShadows.current.set(ctx.guestPeerId, {
    ...sh,
    hidden: !sh.hidden || !isSameHideZone,
    hideZoneId: !sh.hidden || !isSameHideZone ? interactableId : null,
    animationState: 'idle',
  });
  return runtime;
}

/** P2P-хост: действие гостя (полный эффект на мир + инвентарь гостя на хосте). */
export function applyRemoteP2pInteractableEffect(
  runtime: MysteryRoomLocalRuntime,
  interactableId: string,
  guest?: MysteryRoomP2pGuestInteractHostContext,
): MysteryRoomLocalRuntime {
  if (runtime.gameStatus !== 'running') {
    return runtime;
  }
  const it = runtime.interactables.find((e) => e.id === interactableId);
  if (!it) {
    return runtime;
  }
  if (it.state === 'used' && it.kind !== 'hide_zone') {
    return runtime;
  }
  if (it.kind === 'light_switch') {
    return toggleZoneLights(runtime, interactableId);
  }
  if (!guest) {
    return applyAuthoritativeUsedInteractables(runtime, [interactableId]);
  }
  if (it.source === 'custom_object') {
    return activateCustomObjectInteractableForGuest(runtime, interactableId, guest);
  }
  switch (it.kind) {
    case 'access_grant':
      return applyAccessGrantOnHostForGuest(runtime, interactableId, guest);
    case 'hide_zone':
      return toggleHideZoneOnHostForGuest(runtime, interactableId, guest);
    case 'inspect':
      return {
        ...runtime,
        interactables: runtime.interactables.map((entry) =>
          entry.id === interactableId ? { ...entry, state: 'used' as const } : entry,
        ),
      };
    case 'pickup':
      return pickupInventoryItemOnHostForGuest(runtime, interactableId, guest);
    default:
      return applyAuthoritativeUsedInteractables(runtime, [interactableId]);
  }
}

function activateCustomObjectInteractable(runtime: MysteryRoomLocalRuntime, interactableId: string): MysteryRoomLocalRuntime {
  const interactable = runtime.interactables.find((entry) => entry.id === interactableId);
  if (!interactable) {
    return runtime;
  }
  const object = runtime.map.customObjects.find((entry) => entry.id === interactable.customObjectId);
  const actions =
    interactable.kind === 'pickup' && interactable.onPickupActions?.length
      ? interactable.onPickupActions
      : interactable.onActivateActions ?? [];
  const sourceRect = object?.rect ?? {
    x: interactable.anchor.x - 8,
    y: interactable.anchor.y - 8,
    width: 16,
    height: 16,
  };
  const runtimeWithPickup =
    interactable.kind === 'pickup'
      ? upsertInventoryItem(
          runtime,
          inventoryDefinitionForInteractable(runtime, interactable, interactable.itemId ?? object?.id ?? interactable.id, object),
        ).runtime
      : runtime;
  const runtimeWithAccess =
    interactable.kind === 'pickup' && interactable.grantsAccess
      ? applyPickupAccessGrant(runtimeWithPickup, interactable.grantsAccess)
      : runtimeWithPickup;
  const next =
    actions.length > 0
      ? applyLocalTriggerActions(
          runtimeWithAccess,
          {
            id: interactable.customObjectId ?? interactable.id,
            label: interactable.label,
            zoneId: interactable.zoneId,
            rect: sourceRect,
            position: interactable.anchor,
          },
          actions,
        )
      : interactable.kind === 'pickup'
        ? runtimeWithAccess
        : appendLocalEvent(runtime, `Активировано: ${interactable.label}`);

  return {
    ...next,
    interactables: next.interactables.map((entry) =>
      entry.id === interactableId && interactable.kind === 'pickup' ? { ...entry, state: 'used' as const } : entry,
    ),
  };
}

function applyPickupAccessGrant(
  runtime: MysteryRoomLocalRuntime,
  grantsAccess: NonNullable<MysteryRoomLocalRuntime['interactables'][number]['grantsAccess']>,
): MysteryRoomLocalRuntime {
  if (runtime.hero.accessLevels.includes(grantsAccess)) {
    return runtime;
  }

  return {
    ...runtime,
    hero: {
      ...runtime.hero,
      accessLevels: [...runtime.hero.accessLevels, grantsAccess],
    },
    events: [`Доступ активирован: ${grantsAccess}`, ...runtime.events].slice(0, 10),
  };
}

export function getPrimaryLocalAction(runtime: MysteryRoomLocalRuntime): MysteryRoomPrimaryLocalAction | null {
  if (runtime.gameStatus !== 'running' || !runtime.hero.alive) {
    return null;
  }

  const attackableNpc = getNearestLocalNpcInAttackRange(runtime);
  if (attackableNpc && hasInventoryWeapon(runtime)) {
    return {
      kind: 'npc_attack',
      label: `Нейтрализовать: ${attackableNpc.name}`,
      detail: 'weapon · ready',
      targetId: attackableNpc.id,
    };
  }

  const interactable = getNearestLocalInteractable(runtime);
  if (interactable) {
    return {
      kind: 'interactable',
      label: interactable.label,
      detail: `${interactable.kind} · ready`,
      targetId: interactable.id,
    };
  }

  const door = getNearestLocalDoor(runtime);
  if (door) {
    const canUse = canDoorActorUseDoor(door, heroDoorActor(runtime));
    const action = door.state === 'open' || door.state === 'opening' ? 'закрыть' : canUse ? 'открыть' : 'доступ запрещён';
    return {
      kind: 'door',
      label: `${door.label}: ${action}`,
      detail: `${doorAccessRequirementLabel(door)} · ${door.state}`,
      targetId: door.id,
    };
  }

  if (attackableNpc) {
    return {
      kind: 'npc_requires_weapon',
      label: `Нужен предмет против ${attackableNpc.name}`,
      detail: 'weapon · missing',
      targetId: attackableNpc.id,
    };
  }

  return null;
}

export function getAvailableLocalHeroActions(runtime: MysteryRoomLocalRuntime): MysteryRoomHeroAction[] {
  if (runtime.gameStatus !== 'running' || !runtime.hero.alive) {
    return [];
  }

  return runtime.map.incidentAnchors
    .map((anchor) => ({
      anchor,
      distance: Math.hypot(
        runtime.hero.position.x - anchor.position.x,
        runtime.hero.position.y - anchor.position.y,
      ),
    }))
    .filter(({ anchor, distance }) => anchor.zoneId === runtime.hero.zoneId && distance <= HERO_ACTION_RADIUS)
    .flatMap(({ anchor, distance }) => {
      const rule = HERO_ACTION_RULES[anchor.id];
      return rule
        ? [
            {
              id: rule.id,
              label: rule.label,
              anchorId: anchor.id,
              zoneId: anchor.zoneId,
              distance,
            },
          ]
        : [];
    });
}

export function performLocalHeroAction(
  runtime: MysteryRoomLocalRuntime,
  actionId: MysteryRoomHeroActionId,
): MysteryRoomLocalRuntime {
  if (runtime.gameStatus !== 'running' || !runtime.hero.alive) {
    return runtime;
  }

  const action = getAvailableLocalHeroActions(runtime).find((entry) => entry.id === actionId);
  if (!action) {
    return runtime;
  }

  const rule = HERO_ACTION_RULES[action.anchorId];
  if (!rule) {
    return runtime;
  }

  return appendLocalEvent(
    upsertIncidentFromAnchor(runtime, {
      anchorId: action.anchorId,
      sourceType: 'player',
      sourceActorId: 'hero',
      severity: rule.severity,
    }),
    rule.eventMessage,
  );
}

export function getVisibleLocalInteractables(runtime: MysteryRoomLocalRuntime) {
  if (!runtime.visibility) {
    return runtime.interactables;
  }

  return runtime.interactables.filter(
    (entry) => !isConsumedInteractable(entry) && isCircleVisible(runtime.visibility, entry.anchor, INTERACTABLE_VISIBILITY_RADIUS),
  );
}

export function getNearestLocalInteractable(runtime: MysteryRoomLocalRuntime) {
  if (runtime.gameStatus !== 'running' || !runtime.hero.alive) {
    return null;
  }

  const inRange = getVisibleLocalInteractables(runtime)
    .filter((entry) => entry.zoneId === runtime.hero.zoneId)
    .map((entry) => ({
      entry,
      distance: Math.hypot(runtime.hero.position.x - entry.anchor.x, runtime.hero.position.y - entry.anchor.y),
    }))
    .filter(({ entry, distance }) => distance <= entry.interactionRadius)
    .sort((a, b) => a.distance - b.distance);

  return inRange[0]?.entry ?? null;
}

export function hasInventoryWeapon(runtime: MysteryRoomLocalRuntime) {
  return runtime.inventory.some((item) => item.isWeapon && (item.quantity ?? 1) > 0);
}

export function hasInventoryFlashlight(runtime: MysteryRoomLocalRuntime) {
  return runtime.inventory.some((item) => item.id === 'flashlight' && (item.quantity ?? 1) > 0);
}

export function getNearestLocalDoor(runtime: MysteryRoomLocalRuntime) {
  const candidates = runtime.doors
    .map((door) => {
      const center = rectCenter(door.panel);
      const distance = Math.hypot(runtime.hero.position.x - center.x, runtime.hero.position.y - center.y);
      return { door, distance };
    })
    .filter(({ door, distance }) => {
      const visible = !runtime.visibility || isRectVisible(runtime.visibility, door.panel, { requireLineOfSight: false });
      return visible && distance <= DOOR_INTERACTION_RADIUS;
    })
    .sort((a, b) => a.distance - b.distance);

  return candidates[0]?.door ?? null;
}

function isConsumedInteractable(interactable: MysteryRoomLocalRuntime['interactables'][number]) {
  return interactable.state === 'used' && interactable.kind !== 'hide_zone';
}

function applyAccessGrant(runtime: MysteryRoomLocalRuntime, interactableId: string) {
  const interactable = runtime.interactables.find((entry) => entry.id === interactableId);
  if (!interactable) {
    return runtime;
  }

  const itemId = interactable.itemId ?? interactable.id;
  const inventoryResult = upsertInventoryItem(runtime, inventoryDefinitionForInteractable(runtime, interactable, itemId));
  const alreadyHasAccess = interactable.grantsAccess ? runtime.hero.accessLevels.includes(interactable.grantsAccess) : true;
  const nextAccessLevels = !interactable.grantsAccess || alreadyHasAccess
    ? runtime.hero.accessLevels
    : [...runtime.hero.accessLevels, interactable.grantsAccess];

  return {
    ...inventoryResult.runtime,
    hero: {
      ...inventoryResult.runtime.hero,
      accessLevels: nextAccessLevels,
    },
    interactables: inventoryResult.runtime.interactables.map((entry) =>
      entry.id === interactableId ? { ...entry, state: 'used' as const } : entry,
    ),
    events: interactable.grantsAccess && !alreadyHasAccess
      ? [`Доступ активирован: ${interactable.grantsAccess}`, ...inventoryResult.runtime.events].slice(0, 10)
      : inventoryResult.runtime.events,
  };
}

function applyInspect(runtime: MysteryRoomLocalRuntime, interactableId: string) {
  const interactable = runtime.interactables.find((entry) => entry.id === interactableId);
  if (!interactable) {
    return runtime;
  }

  return {
    ...runtime,
    interactables: runtime.interactables.map((entry) =>
      entry.id === interactableId ? { ...entry, state: 'used' as const } : entry,
    ),
    events: [
      `Осмотрено: ${interactable.label}`,
      ...runtime.events,
    ].slice(0, 10),
  };
}

function pickupInventoryItem(runtime: MysteryRoomLocalRuntime, interactableId: string) {
  const interactable = runtime.interactables.find((entry) => entry.id === interactableId);
  if (!interactable?.itemId) {
    return runtime;
  }

  const inventoryResult = upsertInventoryItem(
    runtime,
    inventoryDefinitionForInteractable(runtime, interactable, interactable.itemId),
  );

  return {
    ...inventoryResult.runtime,
    interactables: inventoryResult.runtime.interactables.map((entry) =>
      entry.id === interactableId ? { ...entry, state: 'used' as const } : entry,
    ),
  };
}

function toggleZoneLights(runtime: MysteryRoomLocalRuntime, interactableId: string) {
  const switchInteractable = runtime.interactables.find((entry) => entry.id === interactableId);
  if (!switchInteractable) {
    return runtime;
  }

  const targetLights = getLocalLightSwitchTargetLights(runtime, switchInteractable.id);
  if (targetLights.length === 0) {
    return appendLocalEvent(runtime, `${switchInteractable.label}: линия не отвечает`);
  }

  const targetIds = new Set(targetLights.map((light) => light.id));
  const shouldEnable =
    switchInteractable.lightReaction === 'on'
      ? true
      : switchInteractable.lightReaction === 'off'
        ? false
        : targetLights.some((light) => !light.enabled);
  return {
    ...runtime,
    lights: runtime.lights.map((light) => (targetIds.has(light.id) ? { ...light, enabled: shouldEnable } : light)),
    events: [
      `${shouldEnable ? 'Свет включён' : 'Свет выключен'}: ${switchInteractable.label}`,
      ...runtime.events,
    ].slice(0, 10),
  };
}

export function getLocalLightSwitchTargetLights(runtime: MysteryRoomLocalRuntime, interactableId: string) {
  const switchInteractable = runtime.interactables.find((entry) => entry.id === interactableId);
  if (!switchInteractable || switchInteractable.kind !== 'light_switch') {
    return [];
  }

  const linkedLightIds = new Set(switchInteractable.linkedLightIds ?? []);
  return runtime.lights.filter((light) => {
    if (linkedLightIds.has(light.id)) {
      return true;
    }
    if (light.switchId === switchInteractable.id || light.switchIds?.includes(switchInteractable.id)) {
      return true;
    }
    const hasExplicitSwitch = Boolean(light.switchId || light.switchIds?.length);
    return !hasExplicitSwitch && light.zoneId === switchInteractable.zoneId;
  });
}

export function isLocalLightSwitchEnabled(runtime: MysteryRoomLocalRuntime, interactableId: string) {
  const targetLights = getLocalLightSwitchTargetLights(runtime, interactableId);
  return targetLights.length > 0 && targetLights.some((light) => light.enabled);
}

function toggleHideZone(runtime: MysteryRoomLocalRuntime, interactableId: string) {
  const isSameHideZone = runtime.hero.hideZoneId === interactableId;
  return {
    ...runtime,
    hero: {
      ...runtime.hero,
      hidden: !runtime.hero.hidden || !isSameHideZone,
      hideZoneId: !runtime.hero.hidden || !isSameHideZone ? interactableId : null,
      animationState: 'idle' as const,
    },
    events: [
      isSameHideZone && runtime.hero.hidden ? 'Скрытность отключена' : 'Игрок вошёл в укрытие',
      ...runtime.events,
    ].slice(0, 10),
  };
}

export function toggleLocalDoor(runtime: MysteryRoomLocalRuntime, doorId: string) {
  const door = runtime.doors.find((entry) => entry.id === doorId);
  if (!door) {
    return runtime;
  }

  if (!canDoorActorUseDoor(door, heroDoorActor(runtime))) {
    return {
      ...runtime,
      doors: runtime.doors.map((entry) =>
        entry.id === doorId ? { ...entry, state: 'locked' as const, openProgress: 0, holdOpenMs: 0 } : entry,
      ),
      events: [`Дверь заблокирована: ${door.label} · ${doorAccessRequirementLabel(door)}`, ...runtime.events].slice(0, 10),
    };
  }

  const shouldClose = door.state === 'open' || door.state === 'opening';
  return {
    ...runtime,
    doors: runtime.doors.map((entry) =>
      entry.id === doorId
        ? {
            ...entry,
            state: shouldClose ? ('closing' as const) : ('opening' as const),
            holdOpenMs: shouldClose ? 0 : 1600,
          }
        : entry,
    ),
    events: [`Дверь ${shouldClose ? 'закрывается' : 'открывается'} вручную: ${door.label}`, ...runtime.events].slice(0, 10),
  };
}

/** P2P-хост: переключить дверь по действию гостя (без проверки доступа героя хоста). */
export function applyRemoteP2pDoorToggle(runtime: MysteryRoomLocalRuntime, doorId: string): MysteryRoomLocalRuntime {
  const door = runtime.doors.find((entry) => entry.id === doorId);
  if (!door) {
    return runtime;
  }
  const shouldClose = door.state === 'open' || door.state === 'opening';
  return {
    ...runtime,
    doors: runtime.doors.map((entry) =>
      entry.id === doorId
        ? {
            ...entry,
            state: shouldClose ? ('closing' as const) : ('opening' as const),
            holdOpenMs: shouldClose ? 0 : 1600,
          }
        : entry,
    ),
  };
}

function heroDoorActor(runtime: MysteryRoomLocalRuntime): Pick<DoorActor, 'actorType' | 'accessLevels' | 'inventoryItemIds'> {
  return {
    actorType: 'hero',
    accessLevels: runtime.hero.accessLevels,
    inventoryItemIds: runtime.inventory.filter((item) => (item.quantity ?? 1) > 0).map((item) => item.id),
  };
}

function inventoryDefinitionForInteractable(
  runtime: MysteryRoomLocalRuntime,
  interactable: MysteryRoomLocalRuntime['interactables'][number],
  itemId: string,
  object = runtime.map.customObjects.find((entry) => entry.id === interactable.customObjectId),
): InventoryItemDefinition {
  const resourceKind = interactable.resourceKind ?? object?.resourceKind;
  if (resourceKind) {
    const resource = MYSTERY_ROOM_RESOURCE_DEFINITIONS[resourceKind];
    const amount = normalizePickupQuantity(interactable.resourceAmount ?? object?.resourceAmount);
    return {
      id: mysteryRoomResourceItemId(resourceKind),
      label: resource.label,
      kind: 'resource',
      isWeapon: false,
      icon: inventoryIconOverride(interactable, object) ?? resource.icon,
      iconAssetId: inventoryIconAssetValue(interactable.inventoryIconAssetId, object?.inventoryIconAssetId),
      iconAssetUrl: inventoryIconAssetValue(interactable.inventoryIconAssetUrl, object?.inventoryIconAssetUrl),
      iconAssetName: inventoryIconAssetValue(interactable.inventoryIconAssetName, object?.inventoryIconAssetName),
      iconAssetMimeType: inventoryIconAssetValue(interactable.inventoryIconAssetMimeType, object?.inventoryIconAssetMimeType),
      description: `Ресурс строительства: ${resource.label.toLowerCase()}.`,
      sourceObjectId: object?.id ?? interactable.id,
      quantity: amount,
    };
  }

  const knownItem = INVENTORY_ITEM_DEFINITIONS[itemId];
  if (knownItem) {
    return applyInventoryIconOverrides(knownItem, interactable, object);
  }

  const kind = interactable.pickupKind ?? (object?.kind === 'weapon'
    ? 'weapon'
    : object?.kind === 'badge' || interactable.kind === 'access_grant'
      ? 'access_card'
      : 'tool');
  const accessCardVisual = kind === 'access_card' ? getMysteryRoomAccessCardVisual(interactable.grantsAccess) : null;

  return {
    id: itemId as MysteryRoomInventoryItemState['id'],
    label: interactable.label,
    kind,
    isWeapon: kind === 'weapon',
    icon: inventoryIconOverride(interactable, object) ?? inventoryIconForItem(kind, interactable.label),
    iconAssetId: inventoryIconAssetValue(interactable.inventoryIconAssetId, object?.inventoryIconAssetId),
    iconAssetUrl: inventoryIconAssetValue(interactable.inventoryIconAssetUrl, object?.inventoryIconAssetUrl, accessCardVisual?.inventoryUrl),
    iconAssetName: inventoryIconAssetValue(interactable.inventoryIconAssetName, object?.inventoryIconAssetName, accessCardVisual?.inventoryName),
    iconAssetMimeType: inventoryIconAssetValue(
      interactable.inventoryIconAssetMimeType,
      object?.inventoryIconAssetMimeType,
      accessCardVisual?.mimeType,
    ),
    description: kind === 'access_card'
      ? 'Пропуск карты. Используется дверями, связанными с этой карточкой.'
      : object?.label ? `Предмет карты: ${object.label}` : `Предмет карты: ${interactable.label}`,
    sourceObjectId: object?.id ?? interactable.id,
  };
}

function applyInventoryIconOverrides(
  definition: InventoryItemDefinition,
  interactable: MysteryRoomLocalRuntime['interactables'][number],
  object?: MysteryRoomLocalRuntime['map']['customObjects'][number],
): InventoryItemDefinition {
  return {
    ...definition,
    icon: inventoryIconOverride(interactable, object) ?? definition.icon,
    iconAssetId: inventoryIconAssetValue(interactable.inventoryIconAssetId, object?.inventoryIconAssetId, definition.iconAssetId),
    iconAssetUrl: inventoryIconAssetValue(interactable.inventoryIconAssetUrl, object?.inventoryIconAssetUrl, definition.iconAssetUrl),
    iconAssetName: inventoryIconAssetValue(interactable.inventoryIconAssetName, object?.inventoryIconAssetName, definition.iconAssetName),
    iconAssetMimeType: inventoryIconAssetValue(
      interactable.inventoryIconAssetMimeType,
      object?.inventoryIconAssetMimeType,
      definition.iconAssetMimeType,
    ),
    sourceObjectId: object?.id ?? definition.sourceObjectId,
  };
}

function inventoryIconOverride(
  interactable: MysteryRoomLocalRuntime['interactables'][number],
  object?: MysteryRoomLocalRuntime['map']['customObjects'][number],
): string | undefined {
  const normalized = (interactable.inventoryIcon ?? object?.inventoryIcon ?? '').trim().slice(0, 4).toUpperCase();
  return normalized || undefined;
}

function inventoryIconAssetValue(...values: Array<string | undefined>): string | undefined {
  for (const value of values) {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (normalized) {
      return normalized;
    }
  }
  return undefined;
}

function upsertInventoryItem(runtime: MysteryRoomLocalRuntime, itemDefinition: InventoryItemDefinition) {
  const existingItem = runtime.inventory.find((item) => item.id === itemDefinition.id);
  const amount = normalizePickupQuantity(itemDefinition.quantity);
  const nextQuantity = Math.max(0, existingItem?.quantity ?? 0) + amount;
  const nextInventory = existingItem
    ? runtime.inventory.map((item) =>
        item.id === itemDefinition.id ? { ...item, quantity: nextQuantity } : item,
      )
    : [
        ...runtime.inventory,
        {
          ...itemDefinition,
          acquiredAtMs: runtime.elapsedMs,
          quantity: amount,
          slotIndex: runtime.inventory.length,
        },
      ];
  const isAccessCard = itemDefinition.kind === 'access_card';
  const isResource = itemDefinition.kind === 'resource';
  const pickedPrefix = isResource ? 'Ресурс получен' : isAccessCard ? 'Пропуск получен' : 'Предмет получен';
  const stackedPrefix = isResource ? 'Ресурс сложен' : isAccessCard ? 'Пропуск сложен' : 'Предмет сложен';

  return {
    runtime: {
      ...runtime,
      inventory: nextInventory.map((item, slotIndex) => ({ ...item, slotIndex })),
      events: [
        existingItem
          ? `${stackedPrefix}: ${itemDefinition.label} +${amount} (x${nextQuantity})`
          : `${pickedPrefix}: ${itemDefinition.label} +${amount}`,
        ...runtime.events,
      ].slice(0, 10),
    },
    existingItem,
    quantity: existingItem ? nextQuantity : amount,
  };
}

function inventoryIconForItem(kind: MysteryRoomInventoryItemState['kind'], label: string) {
  if (kind === 'resource') {
    return 'RS';
  }
  if (kind === 'access_card') {
    return 'ID';
  }
  if (kind === 'weapon') {
    return 'WP';
  }

  const letters = label
    .replace(/[^A-Za-zА-Яа-я0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
  return letters || 'IT';
}

function normalizePickupQuantity(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return 1;
  }
  return Math.max(1, Math.floor(value));
}

function neutralizeLocalNpc(runtime: MysteryRoomLocalRuntime, npcId: string) {
  const npc = runtime.npcs.find((entry) => entry.id === npcId);
  if (!npc || !npc.alive) {
    return runtime;
  }

  return {
    ...runtime,
    npcs: runtime.npcs.map((entry) =>
      entry.id === npcId
        ? {
            ...entry,
            health: 0,
            alive: false,
            killedAtMs: runtime.elapsedMs,
            velocity: { x: 0, y: 0 },
            animationState: 'idle' as const,
            awarenessState: 'patrol' as const,
            seesHero: false,
            suspicionMs: 0,
            investigateTarget: null,
            currentTask: null,
            navPath: [],
            navIndex: 0,
            currentTargetNodeId: null,
            currentRoleAction: null,
            roleActionRemainingMs: 0,
          }
        : entry,
    ),
    hero: {
      ...runtime.hero,
      spottedByNpcIds: runtime.hero.spottedByNpcIds.filter((id) => id !== npcId),
      alertState: runtime.hero.spottedByNpcIds.length > 1 ? runtime.hero.alertState : 'clear',
    },
    events: [`NPC нейтрализован: ${npc.name}`, ...runtime.events].slice(0, 10),
  };
}

function getNearestLocalNpcInAttackRange(runtime: MysteryRoomLocalRuntime) {
  const candidates = runtime.npcs
    .filter((npc) => npc.alive && npc.zoneId === runtime.hero.zoneId)
    .filter((npc) => !runtime.visibility || isCircleVisible(runtime.visibility, npc.position, npc.radius))
    .map((npc) => ({
      npc,
      distance: Math.hypot(runtime.hero.position.x - npc.position.x, runtime.hero.position.y - npc.position.y),
    }))
    .filter(({ distance }) => distance <= NPC_ATTACK_RADIUS)
    .sort((a, b) => a.distance - b.distance);

  return candidates[0]?.npc ?? null;
}

function appendLocalEvent(runtime: MysteryRoomLocalRuntime, message: string): MysteryRoomLocalRuntime {
  return {
    ...runtime,
    events: [message, ...runtime.events].slice(0, 10),
  };
}
